// ── Аккаунт и настройки; новый пароль по ссылке из письма ──────────────────────

import { Suspense, useEffect, useState } from "react";
import { getByokKey, setByokKey } from "../aiExplain";
import { LangPicker } from "../components/Lang";
import { NewPerDay } from "../components/Today";
import CustomAISettings from "../CustomAISettings";
import { SYSTEM_LANG, UI_LANG_OPTIONS, getUiLangSetting, setUiLang, t } from "../i18n";
import Icon from "../Icon";
import { lazyWithReload } from "../lazyWithReload";
import { getProfile, saveProfileName } from "../library";
import { getFsrsSettings } from "../srs";
import { requestPasswordReset, supabase } from "../supabase";
import { SANS, THEMES, THEME_META, THEME_ORDER, themeKit } from "../themes";
import { uiSoundsEnabled } from "../soundGate";
import Checkbox from "../ui/Checkbox";
import { markSetting } from "../settings";
import { Button, Confirm, Field, IconButton, Modal } from "../ui";
import { authErrorText } from "./Auth";
import { usePalette } from "../ui/theme";
import { panelSurface } from '../ui/surface';
import { fieldSurface } from '../ui/surface';

// Окна, которые открывают редко, — тоже отдельными частями: первая загрузка сайта легче.
export const ClaudeSettings = lazyWithReload(() => import("../ClaudeSettings"));

// ── AccountModal — управление аккаунтом (смена пароля, сброс, выход везде) ──
// ── Настройки — блоками, от главного к второстепенному ───────────────────────
// Профиль → Учёба → Оформление → ИИ-помощник → Безопасность → выход и документы.
// Редкое (ключи ИИ, пароль) свёрнуто, чтобы не было «простыни» текста.
export function AccountModal({email,theme,onTheme,isPro=false,onClose,onForceLogoutAll,onOpenFsrsSettings,onLogout,activeLang,onActiveLang,langCounts={}}){
  const C=usePalette();
  const [open,setOpen]=useState({ai:false,security:false});
  const [sounds,setSounds]=useState(uiSoundsEnabled);
  const toggleSounds=(v)=>{setSounds(v);try{localStorage.setItem("ac_ui_sounds",v?"1":"0");markSetting("ac_ui_sounds");}catch{}};
  const toggle=(k)=>setOpen(o=>({...o,[k]:!o[k]}));

  // ── Профиль: имя под общими разборами ──
  const [uid,setUid]=useState(null);
  const [name,setName]=useState("");
  const [nameState,setNameState]=useState("");
  useEffect(()=>{supabase.auth.getSession().then(async({data})=>{const id=data?.session?.user?.id;if(!id)return;setUid(id);const p=await getProfile(id).catch(()=>null);if(p?.name)setName(p.name);});},[]);
  const saveName=async()=>{if(!uid)return;setNameState("…");try{await saveProfileName(uid,name);setNameState(t("Сохранено"));}catch(e){setNameState(e.message);}setTimeout(()=>setNameState(""),2500);};

  // ── Учёба: сколько новых карточек брать в день ──
  const [newLimit,setNewLimitState]=useState(()=>getFsrsSettings().newLimit);
  useEffect(()=>{const h=()=>setNewLimitState(getFsrsSettings().newLimit);window.addEventListener("ac:settings-updated",h);return()=>window.removeEventListener("ac:settings-updated",h);},[]);

  // ── Безопасность ──
  const [curPw,setCurPw]=useState("");
  const [newPw,setNewPw]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [ok,setOk]=useState("");
  const changePassword=async()=>{
    setErr("");setOk("");
    if(!curPw){setErr(t("Введите текущий пароль"));return;}
    if(!newPw||newPw.length<6){setErr(t("Новый пароль — минимум 6 символов"));return;}
    if(newPw===curPw){setErr(t("Новый пароль совпадает со старым"));return;}
    setBusy(true);
    try{
      const{error:e1}=await supabase.auth.signInWithPassword({email,password:curPw});
      if(e1){setErr(t("Неверный текущий пароль"));setBusy(false);return;}
      const{error:e2}=await supabase.auth.updateUser({password:newPw});
      if(e2){setErr(authErrorText(e2));setBusy(false);return;}
      try{await supabase.auth.signOut({scope:'others'});}catch{}
      setOk(t("Пароль изменён. Другие устройства выйдут из аккаунта в течение часа."));
      setCurPw("");setNewPw("");
    }catch{setErr(t("Нет соединения. Проверьте интернет."));}
    setBusy(false);
  };
  // Вошли через Google — пароля у аккаунта нет: «текущий» спрашивать нечего, даём задать.
  const [hasPw,setHasPw]=useState(true);
  useEffect(()=>{supabase.auth.getSession().then(({data})=>{
    const u=data?.session?.user;if(!u)return;
    const prov=u.app_metadata?.providers||(u.app_metadata?.provider?[u.app_metadata.provider]:[]);
    let set=false;try{set=localStorage.getItem("ac_pw_set_"+u.id)==="1";}catch{}
    setHasPw(set||prov.includes("email")||(u.identities||[]).some(i=>i.provider==="email"));
  });},[]);
  const setFirstPassword=async()=>{
    setErr("");setOk("");
    if(!newPw||newPw.length<6){setErr(t("Новый пароль — минимум 6 символов"));return;}
    setBusy(true);
    const{data,error}=await supabase.auth.updateUser({password:newPw}).catch(e=>({error:e}));
    setBusy(false);
    if(error){setErr(authErrorText(error));return;}
    try{localStorage.setItem("ac_pw_set_"+(data?.user?.id||""),"1");}catch{}
    setHasPw(true);setNewPw("");
    setOk(t("Пароль задан. Теперь можно входить и по почте с паролем — например, в приложении на телефоне."));
  };
  const sendResetEmail=async()=>{
    setErr("");setOk("");setBusy(true);
    try{
      const{error}=await requestPasswordReset(email);
      if(error)setErr(error.message||t("Не удалось отправить письмо"));else setOk(t("Письмо отправлено на {0}.",[email]));
    }catch{setErr(t("Нет соединения."));}
    setBusy(false);
  };
  const [askLogoutOthers,setAskLogoutOthers]=useState(false);
  const logoutOthers=async()=>{
    setErr("");setOk("");setBusy(true);
    try{await supabase.auth.signOut({scope:'others'});setOk(t("Другие устройства выйдут из аккаунта в течение часа."));}catch{setErr(t("Не удалось. Проверьте интернет."));}
    setBusy(false);
  };

  // ── ИИ: свой ключ OpenRouter ──
  const [aiKey,setAiKey]=useState(()=>getByokKey());
  const [aiSaved,setAiSaved]=useState(false);
  const saveAiKey=(v)=>{setByokKey(v);setAiKey(v);setAiSaved(true);setTimeout(()=>setAiSaved(false),1500);};

  // ── Стили ──
  const card={...panelSurface(C),borderRadius:12,overflow:"hidden"};
  const rowSt={display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderTop:`1px solid ${C.borderSoft}`,minHeight:56,boxSizing:"border-box"};
  const label={fontSize:14.5,color:C.text,fontWeight:500};
  const hint={fontSize:12.5,color:C.faint,marginTop:2,lineHeight:1.4};
  const input={height:40,...fieldSurface(C),borderRadius:8,color:C.text,padding:"0 12px",fontSize:14.5,fontFamily:SANS,outline:"none",boxSizing:"border-box",minWidth:0};
  const seg=(on)=>({flex:1,minWidth:0,height:34,padding:"0 4px",border:"none",borderRadius:7,cursor:"pointer",fontFamily:SANS,fontSize:13.5,fontWeight:on?600:500,background:on?C.card:"transparent",color:on?C.text:C.mid,boxShadow:on?`0 0 0 1px ${C.border}`:"none"});
  const head=(icon,title,sub,key)=>(
    <button onClick={key?()=>toggle(key):undefined} aria-expanded={key?open[key]:undefined} disabled={!key}
      style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"14px",background:"none",border:"none",textAlign:"left",cursor:key?"pointer":"default",fontFamily:SANS,color:C.text}}>
      <span style={{width:34,height:34,borderRadius:9,background:C.surface,color:C.blueDark,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Icon n={icon} s={17}/></span>
      <span style={{flex:1,minWidth:0}}>
        <span style={{display:"block",fontSize:15.5,fontWeight:600}}>{title}</span>
        {sub&&<span style={{display:"block",fontSize:12.5,color:C.faint,marginTop:1}}>{sub}</span>}
      </span>
      {key&&<span style={{color:C.faint,display:"inline-flex"}}><Icon n={open[key]?"chevronUp":"chevronDown"} s={18}/></span>}
    </button>
  );
  const linkRow=(icon,title,sub,onClick)=>(
    <button onClick={onClick} className="ac-ibtn" style={{...rowSt,width:"100%",background:"none",border:"none",borderTop:`1px solid ${C.borderSoft}`,textAlign:"left",cursor:"pointer",fontFamily:SANS,color:C.text}}>
      <span style={{flex:1,minWidth:0}}><span style={{...label,display:"block"}}>{title}</span>{sub&&<span style={{...hint,display:"block"}}>{sub}</span>}</span>
      <span style={{color:C.faint,display:"inline-flex"}}><Icon n="chevronRight" s={18}/></span>
    </button>
  );

  return(
    <Modal size="md" fullOnPhone title={t("Настройки")} onClose={onClose} padded={false} bodyStyle={{ background: C.pageBg }}>
        <div style={{padding:"14px 14px calc(24px + env(safe-area-inset-bottom, 0px))",display:"flex",flexDirection:"column",gap:12}}>
          {/* 1. Профиль */}
          <div data-panel="" style={card}>
            {head("user",t("Профиль"),email||"—")}
            <div style={{...rowSt,flexWrap:"wrap"}}>
              <div style={{flex:"1 1 200px",minWidth:0}}>
                <div style={label}>{t("Имя")}</div>
                <div style={hint}>{t("Видно под вашими разборами слов в общей библиотеке. Почту никто не видит.")}</div>
              </div>
              <div style={{display:"flex",gap:6,flex:"1 1 220px",minWidth:0}}>
                <input value={name} onChange={e=>setName(e.target.value)} maxLength={60} placeholder={t("Например, Антон")} aria-label={t("Имя")} style={{...input,flex:"1 1 auto",width:0}}/>
                <Button onClick={saveName} disabled={!uid}>{nameState||t("Сохранить")}</Button>
              </div>
            </div>
          </div>

          {/* 2. Учёба */}
          <div data-panel="" style={card}>
            {head("cards",t("Учёба"),t("Язык, новые в день, повторение"))}
            <div style={rowSt}>
              <div style={{flex:1,minWidth:0}}><div style={label}>{t("Изучаемый язык")}</div><div style={hint}>{t("Главная, повторение и статистика показывают его.")}</div></div>
              <LangPicker activeLang={activeLang} onChange={onActiveLang} langCounts={langCounts} theme={theme} alignRight/>
            </div>
            <div style={rowSt}>
              <div style={{flex:1,minWidth:0}}><div style={label}>{t("Новых карточек в день")}</div><div style={hint}>{t("Плюс повторение уже пройденных — сколько им пора")}</div></div>
              <NewPerDay C={C} value={newLimit}/>
            </div>
            {onOpenFsrsSettings&&linkRow("brain",t("Алгоритм повторения"),t("Сколько помнить, лимиты новых карточек, начало дня"),onOpenFsrsSettings)}
          </div>

          {/* 3. Оформление */}
          <div data-panel="" style={card}>
            {head(THEME_META[theme]?.icon||"sun",t("Оформление"))}
            <div style={rowSt}>
              <div style={{flex:"0 1 auto",minWidth:0}}><div style={label}>{t("Тема")}</div></div>
              {/* Три темы в ряд (THEME_ORDER); на телефоне переключатель занимает всю ширину строки. */}
              <div role="group" aria-label={t("Тема")} style={{display:"flex",gap:2,padding:3,background:C.surface,borderRadius:9,flex:"1 1 200px",maxWidth:300,minWidth:0}}>
                {/* темы Pro без тарифа — с замком: нажатие объясняет, что это Pro */}
                {THEME_ORDER.map(k=>{const locked=THEME_META[k].pro&&!isPro;return(
                  <button key={k} onClick={()=>onTheme?.(k)} aria-pressed={theme===k} title={locked?t("Только в Pro"):undefined}
                    style={{...seg(theme===k),opacity:locked?0.6:1,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:4}}>
                    {locked&&<Icon n="lock" s={12}/>}{t(THEME_META[k].name)}
                  </button>);})}
              </div>
            </div>
            {themeKit(THEMES[theme]).sounds!=="none"&&(
              <div style={rowSt}>
                <Checkbox checked={sounds} onChange={e=>toggleSounds(e.target.checked)} hint={t("Щелчки кнопок и ноты ответов. Молчат, пока играет аудио.")}>{t("Звуки интерфейса")}</Checkbox>
              </div>
            )}
            <div style={rowSt}>
              <div style={{flex:1,minWidth:0}}>
                <div style={label}>{t("Язык интерфейса")}</div>
                <div style={hint}>{t("Приложение перезапустится на выбранном языке.")}</div>
              </div>
              <select value={getUiLangSetting()} onChange={e=>setUiLang(e.target.value)} aria-label={t("Язык интерфейса")}
                style={{...input,width:220,cursor:"pointer",paddingRight:8}}>
                {UI_LANG_OPTIONS.map(([v,name])=>(
                  <option key={v} value={v}>{v==="auto"?`${t("Как в системе")} (${({ru:"Русский",en:"English",es:"Español"})[SYSTEM_LANG]})`:name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 4. ИИ-помощник (свёрнуто) */}
          <div data-panel="" style={card}>
            {head("sparkles",t("ИИ-помощник"),t("Своя нейросеть или ключи — по желанию"),"ai")}
            {open.ai&&(
              <div style={{borderTop:`1px solid ${C.borderSoft}`,padding:"12px 14px",display:"flex",flexDirection:"column",gap:14}}>
                <CustomAISettings pal={C}/>
                <div style={{height:1,background:C.borderSoft}}/>
                <Suspense fallback={null}><ClaudeSettings pal={C}/></Suspense>
                <div>
                  <div style={label}>{t("Ключ OpenRouter")}</div>
                  <div style={hint}>{t("Без ключа ИИ-объяснения работают через общий лимит сайта. Свой ключ — бесплатно на")} <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" style={{color:C.blueDark}}>openrouter.ai/keys</a>{t(". Хранится только на этом устройстве.")}</div>
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <input type="password" value={aiKey} onChange={e=>setAiKey(e.target.value)} placeholder="sk-or-v1-…" autoComplete="off" spellCheck={false} aria-label={t("Ключ OpenRouter")} style={{...input,flex:1}}/>
                    <Button variant="primary" onClick={()=>saveAiKey(aiKey.trim())} disabled={!aiKey.trim()}>{aiSaved?t("Сохранено"):t("Сохранить")}</Button>
                    {getByokKey()&&<Button onClick={()=>saveAiKey("")}>{t("Удалить")}</Button>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 5. Безопасность (свёрнуто) */}
          <div data-panel="" style={card}>
            {head("shield",t("Безопасность"),t("Пароль и устройства"),"security")}
            {open.security&&(
              <div style={{borderTop:`1px solid ${C.borderSoft}`,padding:"12px 14px",display:"flex",flexDirection:"column",gap:10}}>
                {hasPw?(<>
                <div style={label}>{t("Сменить пароль")}</div>
                <input type="password" value={curPw} onChange={e=>setCurPw(e.target.value)} placeholder={t("Текущий пароль")} aria-label={t("Текущий пароль")} autoComplete="current-password" style={input}/>
                <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&changePassword()} placeholder={t("Новый пароль (от 6 символов)")} aria-label={t("Новый пароль")} autoComplete="new-password" style={input}/>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Button variant="primary" disabled={busy} onClick={changePassword}>{t("Сменить пароль")}</Button>
                  <Button disabled={busy} onClick={sendResetEmail}><Icon n="mail" s={15}/>{t("Забыли? Письмо для сброса")}</Button>
                </div>
                </>):(<>
                <div style={label}>{t("Задать пароль")}</div>
                <div style={hint}>{t("Вы входите через Google — пароля у аккаунта пока нет. Задайте его, чтобы входить и по почте.")}</div>
                <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&setFirstPassword()} placeholder={t("Новый пароль (от 6 символов)")} aria-label={t("Новый пароль")} autoComplete="new-password" style={input}/>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Button variant="primary" disabled={busy} onClick={setFirstPassword}>{t("Задать пароль")}</Button>
                </div>
                </>)}
                {err&&<div role="alert" style={{background:C.surface,border:`1px solid ${C.danger}`,borderRadius:8,padding:"9px 12px",fontSize:13,color:C.danger}}>{err}</div>}
                {ok&&<div role="status" style={{background:C.surface,border:`1px solid ${C.green}`,borderRadius:8,padding:"9px 12px",fontSize:13,color:C.green}}>{ok}</div>}
                <div style={{height:1,background:C.borderSoft,margin:"4px 0"}}/>
                <div style={label}>{t("Устройства")}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Button disabled={busy} onClick={()=>setAskLogoutOthers(true)}>{t("Выйти на других устройствах")}</Button>
                </div>
              </div>
            )}
          </div>

          {/* 6. Выход и документы */}
          {onLogout&&<Button danger full size="lg" icon="logout" onClick={onLogout}>{t("Выйти из аккаунта")}</Button>}
          <div style={{display:"flex",gap:4,alignItems:"center",justifyContent:"center",flexWrap:"wrap",fontSize:12.5,color:C.faint}}>
            <a href="/legal/terms.html" target="_blank" rel="noreferrer" style={{color:C.mid,textDecoration:"none",display:"inline-flex",alignItems:"center",minHeight:36,padding:"0 4px"}}>{t("Условия")}</a>
            <span>·</span>
            <a href="/legal/privacy.html" target="_blank" rel="noreferrer" style={{color:C.mid,textDecoration:"none",display:"inline-flex",alignItems:"center",minHeight:36,padding:"0 4px"}}>{t("Конфиденциальность")}</a>
            <span>·</span>
            <a href="/legal/dmca.html" target="_blank" rel="noreferrer" style={{color:C.mid,textDecoration:"none",display:"inline-flex",alignItems:"center",minHeight:36,padding:"0 4px"}}>{t("Авторские права")}</a>
            <span>·</span>
            <span>{t("Версия")} {(process.env.REACT_APP_BUILD_ID||"dev").slice(0,7)}</span>
          </div>
        </div>
      {askLogoutOthers&&<Confirm title={t("Выйти на других устройствах?")} body={t("Здесь вы останетесь в аккаунте.")} confirmLabel={t("Выйти")}
        onConfirm={logoutOthers} onClose={()=>setAskLogoutOthers(false)}/>}
    </Modal>
  );
}

// ── Новый пароль после ссылки «сбросить пароль» из письма ───────────────────
// Старый пароль не нужен: человек подтвердил почту, перейдя по ссылке.
export function NewPasswordModal({theme,email,onClose}){
  const C=usePalette();
  const [pw,setPw]=useState("");
  const [show,setShow]=useState(false);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [done,setDone]=useState(false);
  const save=async()=>{
    setErr("");
    if(pw.length<6){setErr(t("Пароль слишком простой — минимум 6 символов."));return;}
    setBusy(true);
    const{error}=await supabase.auth.updateUser({password:pw}).catch(e=>({error:e}));
    setBusy(false);
    if(error){setErr(authErrorText(error));return;}
    setDone(true);setTimeout(onClose,1600);
  };
  return(
    <Modal size="sm" title={t("Новый пароль")} onClose={onClose} dismissable={false}
      footer={done?null:<><Button onClick={onClose}>{t("Позже")}</Button><Button variant="primary" onClick={save} busy={busy}>{t("Сохранить пароль")}</Button></>}>
      <div style={{fontSize:14,color:C.mid,lineHeight:1.5,marginBottom:14,overflowWrap:"anywhere"}}>{t("Вы перешли по ссылке из письма. Придумайте новый пароль для {0}.",[email])}</div>
      {done?(
        <div role="status" style={{fontSize:14,color:C.green}}>{t("Пароль сохранён. Входите с ним на всех устройствах.")}</div>
      ):(<>
        <div style={{display:"flex",gap:6,alignItems:"flex-end"}}>
          <Field type={show?"text":"password"} value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()} autoFocus autoComplete="new-password"
            placeholder={t("Новый пароль (от 6 символов)")} aria-label={t("Новый пароль")} style={{flex:"1 1 auto",marginBottom:0}}/>
          <IconButton icon="eye" size={44} label={show?t("Скрыть пароль"):t("Показать пароль")} onClick={()=>setShow(v=>!v)} active={show}/>
        </div>
        {err&&<div role="alert" style={{fontSize:13.5,color:C.danger,lineHeight:1.45,marginTop:10}}>{err}</div>}
      </>)}
    </Modal>
  );
}
